import { useEffect, useState, startTransition } from 'react';
import { convertDashboardData, convertMoneyRows, hasExchangeRate } from '../lib/convertDashboardData';

function deferTask(task) {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(task()), 0);
  });
}

export function useConvertedCurrencyData({
  rawData,
  rawRows,
  displayCurrency,
  rates,
  ratesLoading,
  sourceCurrency,
  setCurrencyConverting,
}) {
  const [convertedData, setConvertedData] = useState(null);
  const [convertedRows, setConvertedRows] = useState([]);

  const needsConversion = displayCurrency !== sourceCurrency;
  const ratesReady = hasExchangeRate(rates, displayCurrency, sourceCurrency);
  const waitingForRates = needsConversion && ratesLoading && !ratesReady;

  useEffect(() => {
    if (!rawData) {
      setConvertedData(null);
      if (!needsConversion) setCurrencyConverting(false);
      return undefined;
    }

    if (!needsConversion) {
      setConvertedData(rawData);
      setCurrencyConverting(false);
      return undefined;
    }

    if (!ratesReady) {
      if (!ratesLoading) {
        setCurrencyConverting(false);
      }
      setConvertedData(rawData);
      return undefined;
    }

    let cancelled = false;

    deferTask(() => {
      if (cancelled) return null;
      return convertDashboardData(rawData, displayCurrency, rates, sourceCurrency);
    }).then((next) => {
      if (cancelled || next == null) return;
      startTransition(() => {
        setConvertedData(next);
        setCurrencyConverting(false);
      });
    });

    return () => {
      cancelled = true;
    };
  }, [
    rawData,
    displayCurrency,
    rates,
    ratesLoading,
    ratesReady,
    needsConversion,
    sourceCurrency,
    setCurrencyConverting,
  ]);

  useEffect(() => {
    if (!rawRows?.length) {
      setConvertedRows([]);
      return undefined;
    }

    if (!needsConversion) {
      setConvertedRows(rawRows);
      return undefined;
    }

    if (!ratesReady) {
      setConvertedRows(rawRows);
      return undefined;
    }

    let cancelled = false;

    deferTask(() => {
      if (cancelled) return null;
      return convertMoneyRows(rawRows, displayCurrency, rates, sourceCurrency);
    }).then((next) => {
      if (cancelled || next == null) return;
      startTransition(() => {
        setConvertedRows(next);
      });
    });

    return () => {
      cancelled = true;
    };
  }, [rawRows, displayCurrency, rates, ratesReady, needsConversion, sourceCurrency]);

  const data = !needsConversion ? rawData : (convertedData ?? rawData);
  const rows = !needsConversion ? rawRows : (convertedRows.length ? convertedRows : rawRows);

  return { data, rows, waitingForRates };
}
